import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import { ENDPOINTS } from "../config/api";

const AuthContext = createContext(null);
const SESSION_KEY = "pms_session";

/**
 * Build the user object from the login account payload.
 * Call enrichUserFromProfile() afterwards to layer in /me data.
 */
function buildUser(token, account) {
  const phone = account.phoneNumber || "";
  const isTenant = Boolean(account.tenantId) && !account.sellerId;

  const tenantInfo = account.tenant || {};
  const sellerInfo = account.seller || {};

  const firstName = isTenant ? tenantInfo.firstName || "" : "";
  const lastName = isTenant ? tenantInfo.lastName || "" : "";
  const email = isTenant ? tenantInfo.email || "" : "";

  const suffix = phone.replace(/\D/g, "").slice(-4);
  const fullName =
    firstName || lastName
      ? `${firstName} ${lastName}`.trim()
      : `Profiler ${suffix}`;
  const initials =
    firstName && lastName
      ? (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
      : suffix.slice(-2).toUpperCase() || "PF";

  return {
    // Core identity
    id: account.id,
    phoneNumber: phone,
    sellerId: account.sellerId || null,
    tenantId: account.tenantId || null,
    branchId: account.branchId || null,
    role: isTenant ? "tenant" : "seller",
    token,
    isArchived: account.isArchived ?? false,
    createdAt: account.createdAt,

    // Display (populated from nested login objects)
    firstName,
    lastName,
    email,
    name: fullName,
    initials,
    business: isTenant
      ? "Tenant Account"
      : sellerInfo.shopName || "Seller Account",
    shopName: sellerInfo.shopName || "",
    storeAddress: sellerInfo.storeAddress || "",
    phone,
    joinDate: account.createdAt
      ? new Date(account.createdAt).toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        })
      : "",
    bio: sellerInfo.note || "",
    location: isTenant ? "" : sellerInfo.storeAddress || "",

    // Enriched fields (filled by /profiler-account/me — default until fetched)
    plan: "",
    planName: "",
    planPrice: null,
    planMaxProducts: null,
    planMaxCategories: null,
    marketName: "",
    marketCity: "",
    marketImage: "",
    marketDescription: "",
    nicheCategories: [],
    products: [],
    productNames: [],
    incomingOrders: [],
    incomingOrderCount: 0,
    requestStatus: null,
    requestIsPaid: null,
    isBanned: false,
    isTenant,
    rating: null,
    verified: false,
  };
}

/**
 * Merge the rich /profiler-account/me (or /profiler-account/{id}) response
 * into an existing user object. Returns a new object.
 */
function enrichUserFromProfile(user, profile) {
  const sellerData = profile.seller || {};
  const planData = sellerData.plan || {};
  const marketData = sellerData.market || {};
  const requestData = sellerData.request || {};

  // For sellers, derive a display name from shop name if no first/last name
  const shopName = sellerData.shopName || user.shopName || "";
  const name = user.firstName ? user.name : shopName || user.name;
  const business =
    user.role === "seller" ? shopName || "Seller Account" : "Tenant Account";
  const bio = sellerData.note || user.bio || "";
  const location = sellerData.storeAddress || user.location || "";

  const planName = planData.name || "";
  // Map plan name to the variant key used by Badge component
  const plan = planName.toLowerCase().includes("premium")
    ? "Premium"
    : planName.toLowerCase().includes("advanced")
      ? "Advanced"
      : planName
        ? "Free"
        : "";

  return {
    ...user,
    name,
    business,
    bio,
    location,
    shopName: shopName,
    storeAddress: sellerData.storeAddress || user.storeAddress || "",
    isBanned: sellerData.isBanned ?? user.isBanned,

    // Plan
    plan,
    planName,
    planPrice: planData.price ?? null,
    planMaxProducts: planData.maxProducts ?? null,
    planMaxCategories: planData.maxCategories ?? null,
    planIsFree: planData.isFree ?? null,

    // Market
    marketName: marketData.name || "",
    marketCity: marketData.city || "",
    marketImage: marketData.image || "",
    marketDescription: marketData.description || "",

    // Request / approval
    requestStatus: requestData.status || null,
    requestIsPaid: requestData.isPaid ?? null,

    // Niche categories & products
    nicheCategories: sellerData.nicheCategories || [],
    products: sellerData.products || [],
    productNames: sellerData.productNames || [],

    // Orders
    incomingOrders: profile.incomingOrders || [],
    incomingOrderCount: profile._count?.incomingOrders ?? 0,

    // isTenant from API (authoritative)
    isTenant: profile.isTenant ?? user.isTenant,
    verified:
      requestData.status === "APPROVED" && (requestData.isPaid ?? false),
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /** Fetch /profiler-account/me and merge into user. Returns enriched user or null on failure. */
  const fetchAndEnrich = useCallback(async (baseUser) => {
    try {
      const res = await fetch(ENDPOINTS.me, {
        headers: {
          Authorization: `Bearer ${baseUser.token}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) return baseUser; // keep base user on error
      const profile = await res.json();
      return enrichUserFromProfile(baseUser, profile);
    } catch {
      return baseUser; // network error — keep base user
    }
  }, []);

  // Restore session from localStorage on mount, then enrich with /me
  useEffect(() => {
    (async () => {
      try {
        const saved = localStorage.getItem(SESSION_KEY);
        if (saved) {
          const { token, account } = JSON.parse(saved);
          if (token && account) {
            const base = buildUser(token, account);
            const enriched = await fetchAndEnrich(base);
            setUser(enriched);
          }
        }
      } catch {
        // Ignore corrupt storage
      }
      setLoading(false);
    })();
  }, [fetchAndEnrich]);

  /**
   * Authenticate with phone + password.
   * Returns { success, role } or { success: false, error }.
   */
  const login = useCallback(
    async (phoneNumber, password) => {
      try {
        const res = await fetch(ENDPOINTS.login, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phoneNumber, password }),
        });

        const data = await res.json();

        if (!res.ok) {
          const msg =
            (Array.isArray(data?.message) ? data.message[0] : data?.message) ||
            "Invalid phone number or password.";
          return { success: false, error: msg };
        }

        const { token, account } = data;

        if (!token || !account) {
          return { success: false, error: "Unexpected response from server." };
        }

        if (account.isArchived) {
          return {
            success: false,
            error: "This account has been deactivated. Contact support.",
          };
        }

        if (!account.sellerId && !account.tenantId) {
          return {
            success: false,
            error: "Account type unknown. Please contact support.",
          };
        }

        // Persist raw login payload for session restore
        localStorage.setItem(SESSION_KEY, JSON.stringify({ token, account }));

        // Build base user, then enrich with /profiler-account/me
        const base = buildUser(token, account);
        const enriched = await fetchAndEnrich(base);
        setUser(enriched);

        return { success: true, role: enriched.role };
      } catch {
        return {
          success: false,
          error: "Network error. Check your connection and try again.",
        };
      }
    },
    [fetchAndEnrich],
  );

  /**
   * Authenticate directly with a bearer token (mobile → web bridge).
   * Calls /profiler-account/me with the token, builds and persists the session.
   * Returns { success, role } or { success: false }.
   */
  const loginWithToken = useCallback(async (token) => {
    try {
      const res = await fetch(ENDPOINTS.me, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) return { success: false };

      const profile = await res.json();

      // Map /me response to the account shape expected by buildUser
      const account = {
        id: profile.id,
        phoneNumber: profile.phoneNumber || "",
        sellerId: profile.sellerId || null,
        tenantId: profile.tenantId || null,
        branchId: profile.branchId || null,
        isArchived: profile.isArchived ?? false,
        createdAt: profile.createdAt,
        tenant: profile.tenant || {},
        seller: profile.seller || {},
      };

      if (account.isArchived) return { success: false };
      if (!account.sellerId && !account.tenantId) return { success: false };

      localStorage.setItem(SESSION_KEY, JSON.stringify({ token, account }));

      const base = buildUser(token, account);
      const enriched = enrichUserFromProfile(base, profile);
      setUser(enriched);

      return { success: true, role: enriched.role };
    } catch {
      return { success: false };
    }
  }, []);

  /** Re-fetch /profiler-account/me and update the stored user in place. */
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const enriched = await fetchAndEnrich(user);
    setUser(enriched);
  }, [user, fetchAndEnrich]);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(SESSION_KEY);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, loginWithToken, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
